import { AfterViewInit, Directive, ElementRef, Inject, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/platform-browser';

const BRACE = {selected: '.offboard-stage-selected .offboard-stage-number', nav: '.offboard-stage-nav',
  panel: '.offboard-stage-panels', svg: '.offboard-stage-brace', path: 'path', hidden: 'hidden',
  viewBox: 'viewBox', d: 'd', empty: '', px: 'px', space: ' ', separator: '|', stacked: 'offboard-stage-stacked',
  resize: 'resize', scroll: 'scroll', input: 'input', mouseup: 'mouseup', toggle: 'toggle', move: 'M', line: 'L', curve: 'Q',
  zero: 0, one: 1, two: 2, inset: 4, minimumGap: 24, bend: 20, spineRatio: 0.5};

/** Decorative geometry only: never changes workflow state or triggers Angular change detection. */
@Directive({selector: '[offboardingBrace]'})
export class OffboardingBraceDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() offboardingBrace: string;
  private host: HTMLElement;
  private browser: BraceWindow;
  private svg: SVGElement;
  private frame: number;
  private active = false;
  private signature = BRACE.empty;
  private sizes: BraceObserver;
  private mutations: MutationObserver;
  private update = () => this.schedule();

  constructor(element: ElementRef, private zone: NgZone, @Inject(DOCUMENT) pageDocument: Document) {
    this.host = element.nativeElement; this.browser = <BraceWindow>pageDocument.defaultView;
  }
  ngAfterViewInit(): void {
    if (!this.browser) { return; }
    this.svg = <SVGElement>this.host.querySelector(BRACE.svg);
    this.active = true;
    this.zone.runOutsideAngular(() => { this.listen(); this.observe(); this.schedule(); });
  }
  ngOnChanges(): void { this.zone.runOutsideAngular(() => this.schedule()); }
  ngOnDestroy(): void {
    this.active = false;
    if (!this.browser) { return; }
    if (this.frame != null) { this.browser.cancelAnimationFrame(this.frame); }
    if (this.sizes) { this.sizes.disconnect(); }
    if (this.mutations) { this.mutations.disconnect(); }
    this.browser.removeEventListener(BRACE.resize, this.update);
    this.browser.removeEventListener(BRACE.scroll, this.update, true);
    this.host.removeEventListener(BRACE.input, this.update, true);
    this.host.removeEventListener(BRACE.mouseup, this.update, true);
    this.host.removeEventListener(BRACE.toggle, this.update, true);
  }
  private listen(): void {
    this.browser.addEventListener(BRACE.resize, this.update);
    this.browser.addEventListener(BRACE.scroll, this.update, true);
    this.host.addEventListener(BRACE.input, this.update, true);
    this.host.addEventListener(BRACE.mouseup, this.update, true);
    this.host.addEventListener(BRACE.toggle, this.update, true);
  }
  private observe(): void {
    if (this.browser.ResizeObserver) {
      this.sizes = new this.browser.ResizeObserver(this.update);
      this.sizes.observe(this.host);
      this.sizes.observe(<Element>this.host.querySelector(BRACE.panel));
      const nav = this.host.querySelector(BRACE.nav);
      if (nav) { this.sizes.observe(nav); }
    }
    this.mutations = new MutationObserver(this.update);
    this.mutations.observe(this.host, {childList: true, characterData: true, subtree: true});
  }
  private schedule(): void {
    if (!this.active || this.frame != null) { return; }
    this.frame = this.browser.requestAnimationFrame(() => { this.frame = null; this.render(); });
  }
  private render(): void {
    const selected = this.host.querySelector(BRACE.selected);
    const nav = this.host.querySelector(BRACE.nav);
    const panel = this.host.querySelector(BRACE.panel);
    if (!selected || !nav || !panel || !this.svg) { this.hide(); return; }
    if (this.syncLayout(nav, panel)) { this.schedule(); return; }
    const step = selected.getBoundingClientRect();
    const rail = nav.getBoundingClientRect();
    const field = panel.getBoundingClientRect();
    const point = step.top + step.height / BRACE.two - field.top;
    if (field.left - rail.right < BRACE.minimumGap || point < BRACE.two || point > field.height - BRACE.two ||
        step.bottom <= rail.top || step.top >= rail.bottom) { this.hide(); return; }
    this.draw(this.geometry(rail, field, point));
  }
  private syncLayout(rail: Element, field: Element): boolean {
    const stacked = field.getBoundingClientRect().left < rail.getBoundingClientRect().right;
    if (rail.classList.contains(BRACE.stacked) === stacked) { return false; }
    rail.classList.toggle(BRACE.stacked, stacked);
    return true;
  }
  private geometry(rail: ClientRect, field: ClientRect, point: number): BraceGeometry {
    const origin = this.host.getBoundingClientRect();
    return {left: Math.round(rail.right - origin.left + BRACE.inset), top: Math.round(field.top - origin.top),
      width: Math.round(field.left - rail.right - BRACE.inset * BRACE.two),
      height: Math.round(field.height), point: Math.round(point)};
  }
  private draw(box: BraceGeometry): void {
    const signature = [box.left, box.top, box.width, box.height, box.point].join(BRACE.separator);
    if (signature === this.signature) { return; }
    this.signature = signature;
    this.svg.style.left = box.left + BRACE.px; this.svg.style.top = box.top + BRACE.px;
    this.svg.style.width = box.width + BRACE.px; this.svg.style.height = box.height + BRACE.px;
    this.svg.setAttribute(BRACE.viewBox, [BRACE.zero, BRACE.zero, box.width, box.height].join(BRACE.space));
    this.svg.querySelector(BRACE.path).setAttribute(BRACE.d, this.path(box));
    this.svg.removeAttribute(BRACE.hidden);
  }
  private path(box: BraceGeometry): string {
    const edge = box.width - BRACE.one;
    const spine = Math.round(box.width * BRACE.spineRatio);
    const radius = Math.min(BRACE.bend, (box.point - BRACE.one) / BRACE.two,
      (box.height - box.point - BRACE.one) / BRACE.two);
    return [BRACE.move, edge, BRACE.one, BRACE.curve, spine, BRACE.one, spine, BRACE.one + radius,
      BRACE.line, spine, box.point - radius, BRACE.curve, spine, box.point, BRACE.one, box.point,
      BRACE.curve, spine, box.point, spine, box.point + radius,
      BRACE.line, spine, box.height - BRACE.one - radius, BRACE.curve, spine, box.height - BRACE.one,
      edge, box.height - BRACE.one].join(BRACE.space);
  }
  private hide(): void {
    this.signature = BRACE.empty;
    if (this.svg) { this.svg.setAttribute(BRACE.hidden, BRACE.empty); }
  }
}
interface BraceObserver { observe(target: Element): void; disconnect(): void; }
interface BraceWindow extends Window { ResizeObserver?: {new(callback: () => void): BraceObserver}; }
interface BraceGeometry { left: number; top: number; width: number; height: number; point: number; }



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-brace.directive.ts